import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { clearPendingDecisions } from "../lib/guardedMutation";
import { invalidateQueries, setQueryAccountScope } from "../lib/queryCache";

import { authGateway } from "../lib/AuthGateway";
import type { AuthUser } from "../lib/AuthGateway";

type SessionStatus = "loading" | "authenticated" | "anonymous" | "error";

interface SessionValue {
  status: SessionStatus;
  user: AuthUser | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);

  const loadSession = useCallback(async () => {
    try {
      const currentUser = await authGateway.getMe();
      setQueryAccountScope(currentUser?.id ?? null);
      setUser(currentUser);
      setStatus(currentUser ? "authenticated" : "anonymous");
    } catch {
      setUser(null);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    let active = true;
    void authGateway
      .getMe()
      .then((currentUser) => {
        if (!active) return;
        setQueryAccountScope(currentUser?.id ?? null);
        setUser(currentUser);
        setStatus(currentUser ? "authenticated" : "anonymous");
      })
      .catch(() => {
        if (!active) return;
        setUser(null);
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const scheduled = new Set<number>();
    const unsubscribe = authGateway.subscribe((event) => {
      if (event === "SIGNED_OUT") {
        clearPendingDecisions();
        invalidateQueries();
        setQueryAccountScope(null);
        setUser(null);
        setStatus("anonymous");
        return;
      }
      if (event === "SIGNED_IN") clearPendingDecisions();
      if (
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED"
      ) {
        invalidateQueries("auth:");
        const timer = window.setTimeout(() => {
          scheduled.delete(timer);
          void loadSession();
        }, 0);
        scheduled.add(timer);
      }
    });
    return () => {
      unsubscribe();
      for (const timer of scheduled) window.clearTimeout(timer);
    };
  }, [loadSession]);

  const refresh = useCallback(async () => {
    setStatus("loading");
    await loadSession();
  }, [loadSession]);

  const logout = useCallback(async () => {
    await authGateway.logout();
    setQueryAccountScope(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo(
    () => ({ status, user, refresh, logout }),
    [status, user, refresh, logout],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error("useSession must be used inside SessionProvider");
  }
  return session;
}
